import type { ApolloClient, OperationVariables } from '@apollo/client';
import type { DocumentNode } from 'graphql';

import { buildEntityOptionsQuery } from 'data/queries/entityOptions';

/** Une option telle que le serveur la rend. Le libellé n'est JAMAIS construit ici. */
export type EntityOption = {
    value: string;
    label: string;
    hint?: string | null;
    disabled?: boolean | null;
};

export type EntityOptionsResult = {
    options: EntityOption[];
    hasMore: boolean;
};

/** Arguments communs à tous les endpoints `<collection>Options`. */
type EntityOptionsVariables<TScope> = OperationVariables & {
    search: string | null;
    ids: string[] | null;
    limit: number | null;
    scope: TScope | null;
};

/** La query aliase son champ en `result` : la réponse a la même forme pour toutes les collections. */
type EntityOptionsData = { result: EntityOptionsResult };

/**
 * Ce que consomment le filtre de DataTable et l'`UltraSelect`. Le périmètre est déjà refermé dans la
 * fermeture, donc une source est un objet inerte : **stockable dans une configuration de colonne**,
 * sans hook ni `useCallback` à stabiliser.
 */
export type EntitySource = {
    key: string;
    labels: { singular: string; plural: string };
    /**
     * Petit référentiel rendu d'un coup par le serveur (`optionsConfig.preload`). Le consommateur en
     * tire une conséquence : UNE requête, puis filtrage local — sinon chaque frappe repartirait au
     * serveur pour filtrer une liste déjà en mémoire.
     */
    preload: boolean;
    /** Le champ de `Query` interrogé. Exposé pour les messages d'erreur et le test de cohérence. */
    field: string;
    /** La query construite. Exposée pour que le test de cohérence la rejoue contre le schéma réel. */
    document: DocumentNode;
    fetchOptions: (
        client: ApolloClient<object>,
        params: { search: string; ids: string[] },
    ) => Promise<EntityOptionsResult>;
};

type DefineEntitySourceConfig<TScope> = {
    /** Identifiant court de la collection : `site`, `user`, `product`… Sert aux clés de rendu. */
    key: string;
    /** Libellés par défaut du champ et de la puce de filtre. */
    labels: { singular: string; plural: string };
    /** Le champ de `Query` (`siteOptions`), déclaré dans `totadmin.options.gql`. */
    field: string;
    /** Le nom GraphQL de l'input de périmètre (`SiteOptionsScopeInput`), s'il y en a un. */
    scopeType?: string;
    /** À tenir aligné sur l'`optionsConfig.preload` du repository. */
    preload?: boolean;
    /** Ce que cette source accepte de proposer. Ne restreint jamais les valeurs déjà sélectionnées. */
    scope?: TScope;
    limit?: number;
};

/**
 * Déclare une collection comme sélectionnable. La même constante alimente ensuite un champ
 * (`UltraSelect`), une puce de filtre de DataTable, et une FilterBar — sans composant intermédiaire.
 *
 * `TScope` est le type d'input généré (`SiteOptionsScopeInput`) : le périmètre reste vérifié par le
 * compilateur, alors même que la query est construite à l'exécution.
 *
 * `withScope` rend une AUTRE source, liée à un périmètre différent. À garder en constante de module :
 * une source est comparée par référence dans les dépendances d'effet.
 */
export function defineEntitySource<TScope = void>(
    config: DefineEntitySourceConfig<TScope>,
): EntitySource & { withScope: (scope: TScope) => EntitySource } {
    const document = buildEntityOptionsQuery(config.field, config.scopeType);

    const build = (scope: TScope | undefined): EntitySource => ({
        key: config.key,
        labels: config.labels,
        preload: config.preload ?? false,
        field: config.field,
        document,
        fetchOptions: async (client, { search, ids }) => {
            const { data } = await client.query<EntityOptionsData, EntityOptionsVariables<TScope>>({
                query: document,
                variables: {
                    search: search || null,
                    ids: ids.length ? ids : null,
                    limit: config.limit ?? null,
                    scope: scope ?? null,
                },
                // Le référentiel bouge rarement et Apollo indexe par variables : revenir sur une
                // recherche déjà tapée est instantané, et rouvrir une puce ne relance rien.
                fetchPolicy: 'cache-first',
            });

            return data.result;
        },
    });

    return { ...build(config.scope), withScope: (scope: TScope) => build(scope) };
}
